import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Loader from '../../components/Loader/Loader';
import { useAccountRecommendations } from '../../hooks/useSurveySessions';
import styles from './VoiceSurveys.module.scss';

// The API sorts; the table shows its order as is.
const SORTS = {
  fit: 'Best fit',
  search: 'Search order',
  distance: 'Nearest',
  price_asc: 'Price: low to high',
  price_desc: 'Price: high to low',
  budget: 'Closest to budget',
};

const DIMENSION_LABELS = {
  affordability: 'Budget',
  distance: 'Distance',
  activities: 'Activities',
  pets: 'Pets',
  interests: 'Interests',
  housing_type: 'Housing type',
  sq_ft: 'Size',
};

const percent = (value) => `${Math.round(value * 100)}%`;

// The fit score is a weighted mean over only what the person told us: each
// line is one thing they expressed, its weight, and how well this place matched.
const FitExplanation = ({ breakdown }) =>
  breakdown.length === 0 ? (
    'Nothing to score against yet.'
  ) : (
    <div>
      {breakdown.map((part) => (
        <div key={part.dimension}>
          {DIMENSION_LABELS[part.dimension] || part.dimension}: {percent(part.match)} match,{' '}
          {percent(part.share)} of score
        </div>
      ))}
    </div>
  );

// How Recommendations::Scorer works today. Keep in step with its WEIGHTS and
// TARGET_BUDGET_SHARE.
const FIT_EXPLANATION = (
  <div>
    <p>Fit runs from 0 to 1. It averages only what the person has told us, so nothing they left out counts against a place.</p>
    <p>
      Budget (15%): full marks when the cheapest unit that fits uses 70% or more of their monthly budget, less the
      cheaper it is. Closer to the budget beats cheaper.
    </p>
    <p>Distance (20%): full marks at the searched place, falling to zero at the edge of the search radius.</p>
    <p>
      When stated: activities 25%, pets 15%, interests 10%, housing type 10%, size 5%. The weights are rescaled
      over what was stated.
    </p>
    <p>Ties go to the nearer place.</p>
  </div>
);

const formatMoney = (value) =>
  value == null ? '-' : `$${Number(value).toLocaleString('en-US')}`;

// What the list was worked out from, so the advisor can tell why it looks the way it does.
const ProfileLine = ({ results }) => {
  const { profile } = results;
  const parts = [
    results.near.join(' · '),
    profile.radiusMiles && `within ${profile.radiusMiles} mi`,
    profile.age && `age ${profile.age}`,
    profile.gender,
    profile.monthlyIncome ? `${formatMoney(profile.monthlyIncome)}/mo` : 'no budget',
    profile.netWorth ? `${formatMoney(profile.netWorth)} assets` : null,
  ].filter(Boolean);

  return <p className={styles.recap}>{parts.join(' · ')}</p>;
};

// The communities we would recommend to this person right now: their survey,
// plus a call still in progress (the API overlays it by itself). Refreshes
// while a call is live, since each answer can change the list.
export const VoiceSurveyRecommendations = ({ account, live }) => {
  const [sort, setSort] = useState('fit');
  const {
    recommendations: results,
    loading,
    error,
  } = useAccountRecommendations(account?.id, { sort, live });

  if (!account) {
    return <p className={styles.empty}>No account for this caller.</p>;
  }

  return (
    <div>
      <FormControl size='small' className={styles.filters}>
        <InputLabel id='recommendations-sort'>Sort</InputLabel>
        <Select
          labelId='recommendations-sort'
          label='Sort'
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          {Object.entries(SORTS).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Loader isLoading={loading && !results}>
        {error && <p className={styles.empty}>Couldn't load recommendations.</p>}
        {results?.status === 'need_location' && (
          <p className={styles.empty}>No location yet.</p>
        )}
        {results?.status === 'none' && (
          <p className={styles.empty}>No matches near {results.near.join(' or ')}.</p>
        )}
        {results?.status === 'ok' && (
          <>
            <ProfileLine results={results} />
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Community</TableCell>
                  <TableCell>Town</TableCell>
                  <TableCell align='right'>Miles</TableCell>
                  <TableCell align='right'>From</TableCell>
                  <TableCell align='right'>Room in budget</TableCell>
                  <TableCell align='right'>
                    <Tooltip title={FIT_EXPLANATION} arrow>
                      <span className={styles.help}>Fit</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align='right'>
                    <Tooltip title='Position on the consumer search page for the same answers.' arrow>
                      <span className={styles.help}>Web rank</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Why</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.communities.map((community) => (
                  <TableRow key={community.communityId}>
                    <TableCell>
                      <Link to={`/communities/${community.communityId}/edit`}>
                        {community.name}
                      </Link>
                    </TableCell>
                    <TableCell>{community.town || '-'}</TableCell>
                    <TableCell align='right'>{community.distanceMiles ?? '-'}</TableCell>
                    <TableCell align='right'>
                      {formatMoney(community.monthlyFrom)}
                    </TableCell>
                    <TableCell align='right'>
                      {formatMoney(community.affordabilityMargin)}
                    </TableCell>
                    <TableCell align='right'>
                      <Tooltip title={<FitExplanation breakdown={community.fitBreakdown || []} />} arrow>
                        <span className={styles.help}>{community.fitScore}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align='right'>{community.searchRank}</TableCell>
                    <TableCell>
                      {community.reasons.map((reason) => (
                        <Chip
                          key={reason}
                          size='small'
                          label={reason}
                          className={styles.reason}
                        />
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Loader>
    </div>
  );
};
